<template>
    <div class="oauth-login-form">
        <form @submit.prevent="handleSubmit()">
            <div class="oauth-login-form__field">
                <ui-input
                    id="oauth-email"
                    v-model="formData.email"
                    name="email"
                    type="email"
                    placeholder="email@example.com"
                    autocomplete="email"
                    :label="$t('Email')"
                    :field="v$.formData.email"
                    @change="resetErrors()"
                />
            </div>

            <div class="oauth-login-form__field">
                <ui-input
                    id="oauth-password"
                    v-model="formData.password"
                    name="password"
                    type="password"
                    placeholder="********"
                    autocomplete="current-password"
                    :label="$t('Password')"
                    :field="v$.formData.password"
                    @change="resetErrors()"
                />
            </div>

            <div class="oauth-login-form__forgot">
                <b-button
                    variant="link"
                    type="button"
                    class="oauth-login-form__forgot-button"
                    @click="openResetPasswordModal()"
                >
                    {{ $t('Forgot password?') }}
                </b-button>
            </div>

            <vue-recaptcha
                ref="recaptchaComponent"
                class="w-100"
                size="invisible"
                :sitekey="CONST.CAPTCHACODE"
                :loadRecaptchaScript="true"
                @verify="login"
            />

            <ui-button
                id="oauth-login-submit"
                :loading="validating"
                class="oauth-login-form__submit"
                type="submit"
                variant="primary"
            >
                {{ $t(submitText) }}
            </ui-button>
        </form>

        <b-alert class="mt-2" fade :show="!!loginError" variant="danger">
            {{ loginError }}
        </b-alert>

        <b-alert class="mt-2" fade :show="requireUpdatePassword" variant="warning">
            <p class="mb-1">
                {{ $t('To login you need to update your password, to do this you can click on the following link:') }}
            </p>
            <b-button
                variant="link"
                type="button"
                class="oauth-login-form__forgot-button mb-0"
                @click="openResetPasswordModal()"
            >
                {{ $t('Update password') }}
            </b-button>
        </b-alert>

        <b-alert class="mt-2" fade :show="socialAccount" variant="warning">
            <p class="mb-1">
                {{ $t('Your account was created with Google. Sign in with Google to complete your information.') }}
            </p>
        </b-alert>

        <recovery-password-modal
            ref="recoveryPasswordModal"
            :email="formData.email"
            :is-updating="requireUpdatePassword"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import VueRecaptcha from 'vue-recaptcha';
import { useVuelidate } from '@vuelidate/core';
import { email, required, minLength, helpers } from '@vuelidate/validators';

import RecoveryPasswordModal from '@/components/login/RecoveryPasswordModal.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import UiInput from '@/components/ui/inputs/Input.vue';

import webViewMixin from '@/plugins/mixin/web-view.mixin';

import recaptchaUtils from '@/utils/recaptcha.utils';

export default {
    name: 'OauthLoginForm',
    components: {
        RecoveryPasswordModal,
        UiButton,
        UiInput,
        VueRecaptcha,
    },
    mixins: [webViewMixin],
    props: {
        submitText: {
            type: String,
            default: 'Authorize and continue',
        },
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            formData: {
                email: '',
                password: '',
            },
            vuelidateExternalResults: {
                formData: {
                    email: [],
                    password: [],
                },
            },
            validating: false,
            loginError: null,
            requireUpdatePassword: false,
            socialAccount: false,
        };
    },
    validations() {
        return {
            formData: {
                email: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('email') }), required),
                    email: helpers.withMessage(this.$t('Email invalid'), email),
                    $autoDirty: true,
                },
                password: {
                    required: helpers.withMessage(this.$t('validations.required.female', { field: this.$t('password') }), required),
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params), minLength(6)),
                    $autoDirty: true,
                },
            },
        };
    },
    methods: {
        ...mapActions('deviceAccounts', ['addAccount']),
        resetErrors() {
            this.loginError = null;
            this.requireUpdatePassword = false;
            this.socialAccount = false;
        },
        openResetPasswordModal() {
            this.$refs.recoveryPasswordModal.show();
        },
        formatRedirectUrl(redirect_url) {
            if (!redirect_url.startsWith('/')) {
                const formatted_url = new URL(redirect_url);
                const current_host = window.location.host;
                const webview = this.$route.query.webview;

                if (webview) {
                    formatted_url.searchParams.set('webview', webview);
                }

                if (formatted_url.host === current_host && formatted_url.pathname !== '/authentication') {
                    return `${formatted_url.pathname}${formatted_url.search}`;
                }
            }

            return redirect_url;
        },
        async validateEmail() {
            const config = { auth: { username: this.formData.email, password: '' } };

            try {
                const { data } = await this.axiosAccount.get('/api/accounts/validate/email', config);
                const { password, webauthn, country } = data;

                if (webauthn) {
                    this.$emit('update:showFinger', { status: true, email: this.formData.email });
                }

                if (!password && country) {
                    this.requireUpdatePassword = true;
                    return false;
                }

                if (!password && !country) {
                    this.socialAccount = true;
                    return false;
                }

                return true;
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    const errors = { formData: { email: [this.$t('This account does not exist')] } };
                    Object.assign(this.vuelidateExternalResults, errors);
                    return false;
                }

                this.showError(error);
                return false;
            }
        },
        async handleSubmit() {
            this.v$.$touch();

            if (this.v$.$invalid) {
                return;
            }

            this.resetErrors();
            this.validating = true;

            const emailIsValid = await this.validateEmail();

            if (!emailIsValid) {
                this.validating = false;
                return;
            }

            if (this.isRunningInWebView || !recaptchaUtils.isAvailable()) {
                this.login('');
                return;
            }

            this.$refs.recaptchaComponent.reset();
            this.$refs.recaptchaComponent.execute();
        },
        async login(response) {
            try {
                this.validating = true;

                const token = `${this.formData.email}:${this.formData.password}`;
                const encodedToken = Buffer.from(token).toString('base64');
                const config = { headers: { 'Authorization': 'Basic ' + encodedToken } };
                const payload = { token: response };

                const { data } = await this.axiosAccount.post('/api/login', payload, config);
                let { redirect_url, gtsr } = data;
                const webview = this.$route.query.webview;

                this.addAccount({ email: this.formData.email });

                if (webview) {
                    redirect_url = this.formatWebviewRedirectUrl(redirect_url);
                }

                if (gtsr) {
                    const query = { redirect_url };
                    const { site_id, lang, webview: webviewParam } = this.$route.query;

                    if (site_id) {
                        query.site_id = site_id;
                    }

                    if (lang) {
                        query.lang = lang;
                    }

                    if (webviewParam) {
                        query.webview = webviewParam;
                    }

                    sessionStorage.setItem('tfa_expiration', this.$moment().add(5, 'minutes'));
                    this.$router.push({ name: 'securityRecommendations', query });
                    return;
                }

                redirect_url = this.formatRedirectUrl(redirect_url);

                if (redirect_url.startsWith('/')) {
                    this.$router.push(redirect_url);
                } else {
                    window.location.replace(redirect_url);
                }
            } catch (error) {
                const status = error?.response?.status;
                const message = error?.response?.data?.message;

                if (status === 401) {
                    const errors = { formData: { password: [message] } };
                    Object.assign(this.vuelidateExternalResults, errors);
                    return;
                }

                if (status === 403) {
                    this.loginError = message;
                    return;
                }

                this.showError(error);
            } finally {
                this.validating = false;
            }
        },
    },
};
</script>

<style lang="scss">
.oauth-login-form {
    text-align: left;

    &__field {
        margin-bottom: 16px;
    }

    &__forgot {
        margin-bottom: 12px;
        text-align: left;
    }

    &__forgot-button {
        color: $accounts-secondary !important;
        font-weight: 500 !important;
        padding: 0 !important;
    }

    &__submit {
        margin-bottom: 8px;
        width: 100%;
    }
}
</style>
